import { Box, Typography, Paper, Grid, Chip, Avatar, IconButton, Divider, Table, TableBody, TableCell, TableHead, TableRow, Button } from '@mui/material';
import type { ChipColor } from '@/types/chip';
import { School, Email, Phone, Close } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { Show, useGetList } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import type { SessionRecord } from '../shared/types';
import type { AttendanceRecord } from '@/types/entities';

const STATUS_COLORS: Record<string, ChipColor> = {
  Draft: 'default',
  Active: 'success',
  Inactive: 'warning',
};

export const StudentDetail = () => {
  const navigate = useNavigate();
  const { data: sessions } = useGetList('tutoring-sessions');
  const { data: attendanceData } = useGetList('attendance');

  return (
    <Show render={({ record }) => {
      if (!record) return null;

      const studentSessions = (sessions || []).filter((s: SessionRecord) => s.students?.some(st => st.id === record.id)).slice(0, 10);
      const studentAttendance = (attendanceData || []).filter((a: AttendanceRecord) => a.student?.id === record.id).slice(0, 10);
      const attendanceSummary = {
        present: studentAttendance.filter((a: AttendanceRecord) => a.present).length,
        absent: studentAttendance.filter((a: AttendanceRecord) => !a.present).length,
        late: 0,
        excused: 0,
      };

      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => navigate('/students')} size="small" sx={{ color: 'text.secondary' }} aria-label="Close">
              <Close />
            </IconButton>
          </Box>

          <PageDescription
            entity="Student"
            description="A learner enrolled in tutoring services. Students are associated with clients (schools or organisations)."
            icon={<School />}
          />

          <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #7b1fa2, #9c27b0)', color: 'primary.contrastText', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{ width: 72, height: 72, bgcolor: 'secondary.main', fontSize: 28 }}>
                {record.firstName?.[0]}{record.lastName?.[0]}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700}>{record.legalEntity?.firstName ?? ""} {record.legalEntity?.lastName ?? ""}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{record.legalEntity?.email || '—'}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={record.status} size="small" color={STATUS_COLORS[record.status]} />
                </Box>
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <School color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Student Information</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">First Name</Typography>
                    <Typography variant="body1">{record.legalEntity?.firstName ?? "—"}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Last Name</Typography>
                    <Typography variant="body1">{record.legalEntity?.lastName ?? "—"}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                    <Typography variant="body1">{record.dateOfBirth || '—'}</Typography>
                  </Grid>
                  {record.parent && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary">Parent / Client</Typography>
                      <Button size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => navigate(`/clients/${record.parent?.id}/show`)}>
                        {record.parent?.id || '—'}
                      </Button>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2">{record.legalEntity?.email || '—'}</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">{record.legalEntity?.phone || '—'}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>



            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Attendance Summary</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 3 }}>
                    <Chip label={`${attendanceSummary.present} Present`} color="success" size="small" sx={{ width: '100%' }} />
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <Chip label={`${attendanceSummary.absent} Absent`} color="error" size="small" sx={{ width: '100%' }} />
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <Chip label={`${attendanceSummary.late} Late`} color="warning" size="small" sx={{ width: '100%' }} />
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <Chip label={`${attendanceSummary.excused} Excused`} color="info" size="small" sx={{ width: '100%' }} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Recent Sessions</Typography>
                  <Chip label={`${studentSessions.length} records`} size="small" sx={{ ml: 1 }} />
                </Box>
                {studentSessions.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                         <TableCell>Start Time</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {studentSessions.map((s: SessionRecord) => (
                        <TableRow key={s.id}>
                          <TableCell>{s.startDateTime || '—'}</TableCell>
                          <TableCell><Chip label={s.status} size="small" color={s.status === 'Completed' ? 'success' : 'default'} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">No sessions found</Typography>
                )}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Attendance Record</Typography>
                  <Chip label={`${studentAttendance.length} records`} size="small" sx={{ ml: 1 }} />
                </Box>
                {studentAttendance.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Session</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {studentAttendance.map((a: AttendanceRecord) => (
                        <TableRow key={a.id}>
                          <TableCell>{a.session?.id || '—'}</TableCell>
                          <TableCell>{a.durationMinutes ? `${a.durationMinutes} min` : '—'}</TableCell>
                          <TableCell>
                            <Chip
                              label={a.present ? 'Present' : 'Absent'}
                              size="small"
                              color={a.present ? 'success' : 'error'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">No attendance records found</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      );
    }} />
  );
};
